import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Tag, Typography, theme } from 'antd';
import { Checkbox, Spinner } from '@shared/ui';
import { useCategoriesQuery, useCategoryName, type Category } from '@entities/taxonomy';

const { Text } = Typography;

interface CategoriesPickerProps {
  value: string[];
  onChange: (ids: string[], info: { selectedEco: boolean }) => void;
  disabled?: boolean;
}

const SECTION_TONES: Record<'PRODUCER' | 'ECO_PRODUCER', { tag: string; border: string }> = {
  PRODUCER: { tag: 'blue', border: '#2563eb' },
  ECO_PRODUCER: { tag: 'green', border: '#10b981' },
};

const SECTION_KEYS: Record<'PRODUCER' | 'ECO_PRODUCER', string> = {
  PRODUCER: 'profile.categories.sectionProducer',
  ECO_PRODUCER: 'profile.categories.sectionEco',
};

export function CategoriesPicker({ value, onChange, disabled }: CategoriesPickerProps) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useCategoriesQuery(['PRODUCER', 'ECO_PRODUCER']);
  const localize = useCategoryName();
  const { token } = theme.useToken();

  // Group categories by kind so the UI can render two clearly labelled sections.
  const grouped = useMemo(() => {
    const out: Record<'PRODUCER' | 'ECO_PRODUCER', Category[]> = {
      PRODUCER: [],
      ECO_PRODUCER: [],
    };
    for (const cat of data ?? []) {
      if (cat.kind === 'PRODUCER' || cat.kind === 'ECO_PRODUCER') {
        out[cat.kind].push(cat);
      }
    }
    return out;
  }, [data]);

  const ecoIds = useMemo(
    () => new Set(grouped.ECO_PRODUCER.map((c) => c.id)),
    [grouped.ECO_PRODUCER],
  );

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Spinner size="small" />
        <Text type="secondary">{t('common.loading')}</Text>
      </div>
    );
  }
  if (isError || !data) {
    return <Alert type="error" showIcon message={t('products.loadFail', { message: '' })} />;
  }

  const toggle = (id: string) => {
    if (disabled) return;
    const next = value.includes(id) ? value.filter((v) => v !== id) : [...value, id];
    const selectedEco = next.some((catId) => ecoIds.has(catId));
    onChange(next, { selectedEco });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {value.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {value.map((id) => {
            const cat = data.find((c) => c.id === id);
            if (!cat) return null;
            const color = cat.kind === 'ECO_PRODUCER' ? 'green' : 'blue';
            return (
              <Tag key={id} color={color} style={{ marginInlineEnd: 0 }}>
                {localize(cat)}
              </Tag>
            );
          })}
        </div>
      ) : null}

      {(['PRODUCER', 'ECO_PRODUCER'] as const).map((kind) => {
        const items = grouped[kind];
        if (items.length === 0) return null;
        const tone = SECTION_TONES[kind];
        return (
          <section
            key={kind}
            style={{
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: 12,
              overflow: 'hidden',
              background: token.colorBgContainer,
            }}
          >
            <header
              style={{
                padding: '8px 14px',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorFillQuaternary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderLeft: `4px solid ${tone.border}`,
              }}
            >
              <Text strong style={{ fontSize: 13 }}>
                {t(SECTION_KEYS[kind])}
              </Text>
              <Tag color={tone.tag} style={{ marginInlineEnd: 0, fontSize: 11 }}>
                {items.filter((c) => value.includes(c.id)).length} / {items.length}
              </Tag>
            </header>
            <ul
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                maxHeight: 240,
                overflowY: 'auto',
              }}
            >
              {items.map((cat) => {
                const checked = value.includes(cat.id);
                return (
                  <li
                    key={cat.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '8px 14px',
                      borderBottom: `1px solid ${token.colorBorderSecondary}`,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      background: checked ? `${tone.border}10` : 'transparent',
                    }}
                    onClick={() => toggle(cat.id)}
                  >
                    {/*
                      Checkbox is intentionally view-only — the click is
                      handled at the row (li) level so it works on both the
                      checkbox and the label. A noop onChange satisfies antd's
                      controlled-checkbox warning without firing toggle twice.
                    */}
                    <Checkbox checked={checked} disabled={disabled} onChange={() => {}} />
                    <span style={{ flex: 1, fontSize: 13 }}>{localize(cat)}</span>
                    <Text type="secondary" style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>
                      {cat.slug}
                    </Text>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
